/**
 * weitzman-quickshop-modal.js
 * Behavior for snippets/weitzman-quickshop-modal.liquid. Render ONCE per
 * page (weitzman-collection.liquid and weitzman-product.liquid each render
 * the snippet once). Any element on the page with
 * [data-quickshop-trigger][data-product-handle] opens it.
 *
 * Fetches the real product via the Ajax API (/products/{handle}.js) so the
 * modal reflects live variants, price, and availability rather than the
 * static data-attributes the original HTML prototype used. The prototype's
 * modal <img> defaulted src to an empty string ("") which made the browser
 * re-request the current page as an image; this build leaves src unset until
 * JS populates it.
 *
 * i18n strings come from the [data-quickshop-modal] element's data-i18n
 * attribute (a JSON blob written by the Liquid snippet), not from Liquid
 * interpolation in this file, since this file is a static asset.
 *
 * Loaded with defer, so window.WTZ.* (from weitzman-2026.js, also deferred)
 * is available by the time any top-level code here runs. Even so,
 * window.WTZ.money / window.WTZ.withWidth are aliased locally inside
 * renderVariant() and openForHandle() rather than once at the top of the
 * IIFE: those two functions are only ever invoked later, from click
 * handlers or after a fetch resolves, so keeping the alias local to each
 * function is the safer, more obviously-correct pattern regardless of load
 * order. Do not hoist these aliases out of their functions.
 */
(function () {
  const modal = document.querySelector('[data-quickshop-modal]');
  if (!modal || modal.dataset.wired === '1') return;
  modal.dataset.wired = '1';

  const img = modal.querySelector('[data-quickshop-img]');
  const thumbs = modal.querySelector('[data-quickshop-thumbs]');
  const title = modal.querySelector('[data-quickshop-title]');
  const price = modal.querySelector('[data-quickshop-price]');
  const options = modal.querySelector('[data-quickshop-options]');
  const desc = modal.querySelector('[data-quickshop-desc]');
  const link = modal.querySelector('[data-quickshop-link]');
  const addBtn = modal.querySelector('[data-quickshop-add]');
  const qtySelect = modal.querySelector('[data-quickshop-qty]');
  const note = modal.querySelector('[data-quickshop-note]');
  const closeBtn = modal.querySelector('[data-quickshop-close]');
  let currentVariant = null;
  /* Engraving / Custom Field products: the fields live on the product page,
     so the modal's button sends the shopper there instead of adding. */
  let currentPersonalised = false;
  let currentProductUrl = '';
  function isPersonalised(product) {
    return (product.tags || []).some(function (t) {
      return t === 'personalized-name' || t === 'personalized-date' || t.indexOf('Custom Field: ') === 0;
    });
  }
  const i18n = JSON.parse(modal.dataset.i18n || '{}');

  let qsLastFocus = null;

  function trapFocus(e) {
    if (window.WTZ && window.WTZ.trapFocus) { window.WTZ.trapFocus(modal, e); }
  }

  function openModal() {
    qsLastFocus = document.activeElement;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
    if (qsLastFocus) { qsLastFocus.focus(); qsLastFocus = null; }
  }

  function setImage(src, alt) {
    img.src = src;
    img.alt = alt || '';
  }

  function renderVariant(product, variant) {
    const money = window.WTZ.money;
    const withWidth = window.WTZ.withWidth;
    currentVariant = variant;
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      price.innerHTML = '<span class="wtz-modal-sale-price">' + money(variant.price) + '</span> <s class="wtz-modal-compare">' + money(variant.compare_at_price) + '</s>';
    } else {
      price.textContent = money(variant.price);
    }
    const available = variant.available;
    addBtn.disabled = !available;
    addBtn.textContent = available ? (currentPersonalised ? (i18n.personalise || 'Personalise') : i18n.addToCart) : i18n.soldOut;
    if (!available && product.tags && product.tags.indexOf('preorder') !== -1) {
      note.hidden = false;
      note.textContent = i18n.preorderNote;
    } else if (!available) {
      note.hidden = false;
      note.textContent = i18n.currentlySoldOut;
    } else {
      note.hidden = true;
    }
    if (variant.featured_image && variant.featured_image.src) {
      setImage(withWidth(variant.featured_image.src, 600), product.title);
    }
  }

  function renderOptions(product, selectedVariant) {
    options.innerHTML = '';
    if (product.options.length === 1 && product.options[0].name === 'Title') return;
    product.options.forEach(function (option, index) {
      const group = document.createElement('div');
      group.className = 'wtz-option-group';
      const label = document.createElement('label');
      label.className = 'wtz-option-label';
      label.textContent = option.name;
      group.appendChild(label);
      const row = document.createElement('div');
      row.className = 'wtz-swatch-row';
      const values = [];
      product.variants.forEach(function (v) {
        const val = v['option' + (index + 1)];
        if (val && values.indexOf(val) === -1) values.push(val);
      });
      const selectedValue = selectedVariant ? selectedVariant['option' + (index + 1)] : values[0];
      values.forEach(function (val) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'wtz-swatch-btn' + (val === selectedValue ? ' is-selected' : '');
        btn.textContent = val;
        btn.dataset.optionIndex = index;
        btn.dataset.value = val;
        btn.addEventListener('click', function () {
          row.querySelectorAll('.wtz-swatch-btn').forEach(function (b) { b.classList.remove('is-selected'); });
          btn.classList.add('is-selected');
          selectVariantFromOptions(product);
        });
        row.appendChild(btn);
      });
      group.appendChild(row);
      options.appendChild(group);
    });
  }

  function selectVariantFromOptions(product) {
    const selected = [];
    options.querySelectorAll('.wtz-option-group').forEach(function (group) {
      const active = group.querySelector('.is-selected');
      selected.push(active ? active.dataset.value : null);
    });
    const match = product.variants.find(function (v) {
      return selected.every(function (val, i) {
        return val === null || v['option' + (i + 1)] === val;
      });
    });
    if (match) { renderVariant(product, match); return; }
    /* No variant has this combination. Clear currentVariant so Add to Cart
       can't fall back to the previously selected one. */
    currentVariant = null;
    addBtn.disabled = true;
    addBtn.textContent = i18n.unavailable || 'Unavailable';
    note.hidden = true;
  }

  function openForHandle(handle) {
    const withWidth = window.WTZ.withWidth;
    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        title.textContent = product.title;
        desc.textContent = product.description ? product.description.replace(/<[^>]*>/g, '').slice(0, 220) : '';
        link.href = product.url;
        setImage(product.featured_image ? withWidth(product.featured_image, 600) : '', product.title);

        thumbs.innerHTML = '';
        (product.images || []).slice(0, 4).forEach(function (src, i) {
          const t = document.createElement('img');
          t.src = withWidth(src, 200);
          t.alt = product.title + ' thumbnail ' + (i + 1);
          if (i === 0) t.classList.add('is-active');
          t.addEventListener('click', function () {
            thumbs.querySelectorAll('img').forEach(function (th) { th.classList.remove('is-active'); });
            t.classList.add('is-active');
            setImage(withWidth(src, 600), product.title);
          });
          thumbs.appendChild(t);
        });

        currentPersonalised = isPersonalised(product);
        currentProductUrl = product.url;
        const firstAvailable = product.variants.find(function (v) { return v.available; }) || product.variants[0];
        renderOptions(product, firstAvailable);
        renderVariant(product, firstAvailable);
        openModal();
      })
      .catch(function () {
        /* ponytail: quick shop degrades to a no-op if the fetch fails, the
           card link still takes the shopper to the full PDP */
      });
  }

  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-quickshop-trigger]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      openForHandle(trigger.dataset.productHandle);
    }
  });

  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  modal.addEventListener('click', function (e) {
    if (e.target === modal) closeModal();
  });
  document.addEventListener('keydown', function (e) {
    if (!modal.classList.contains('is-open')) return;
    if (e.key === 'Escape') closeModal();
    else if (e.key === 'Tab') trapFocus(e);
  });

  if (addBtn) {
    addBtn.addEventListener('click', function () {
      if (currentPersonalised && currentVariant) {
        window.location.href = currentProductUrl + '?variant=' + currentVariant.id;
        return;
      }
      if (!currentVariant || !window.WTZ || !window.WTZ.addToCart) return;
      addBtn.disabled = true;
      const originalLabel = addBtn.textContent;
      /* Confirmation is a toast from WTZ.addToCart (weitzman-cart.js) rather
         than a button-label swap: this modal closes on a successful add, so a
         message left on its button would vanish before it could be read. */
      window.WTZ.addToCart({ id: currentVariant.id, quantity: parseInt(qtySelect.value, 10) || 1 })
        .catch(function (err) {
          const msg = err.description !== undefined
            ? (err.description || i18n.soldOut)
            : (i18n.addFailed || 'Could not add. Try again');
          window.WTZ.toast(msg, { type: 'error' });
        })
        .then(function () {
          addBtn.textContent = originalLabel;
          addBtn.disabled = false;
        });
    });
  }
})();
